<script lang="ts">
	// Formatters/i18n
	import { format } from 'svelte-i18n';

	// Svelte functions
	import { createEventDispatcher, onDestroy } from 'svelte';

	// Design system components
	import { Button, ButtonV2, Modal, Stack, Text } from '@Shelly/shelly-design-system/index';

	// Helpers
	import { escapeHtml } from './shared/home-management-utils';

	export let showModal = false;
	export let unitName = '';
	export let ownedCount = 0;
	export let sharedCount = 0;
	/** When true, render the V2 button variant; otherwise the legacy one. */
	export let useV2 = false;

	const dispatch = createEventDispatcher<{
		cancel: void;
		delete: void;
		switchToHome: void;
	}>();

	/** Hold the delete button this long (ms) before deletion is armed. */
	const HOLD_DURATION_MS = 5_000;
	const HOLD_DURATION_SECONDS = HOLD_DURATION_MS / 1000;

	type HoldPhase = 'idle' | 'holding' | 'complete';

	let holdPhase: HoldPhase = 'idle';
	let holdProgress = 0;
	let holdSecondsRemaining = HOLD_DURATION_SECONDS;
	let rafId: number | undefined;
	let holdStartTs = 0;
	let activePointerId: number | null = null;
	let holdDeleteHostEl: HTMLDivElement;

	// Pick the correct design system button flavor once, then render it via
	// <svelte:component> to avoid duplicating every button for useV2.
	$: ButtonComponent = useV2 ? ButtonV2 : Button;

	$: isHolding = holdPhase !== 'idle';
	$: hasOwnedDevices = ownedCount > 0;

	$: modalTitle = hasOwnedDevices
		? $format('settings_delete_unit_check_title_has_devices', { default: 'Home contains devices' })
		: $format('settings_delete_unit_check_title_no_devices', { default: 'Confirm home deletion' });

	$: holdButtonLabel = getHoldButtonLabel(holdPhase, holdSecondsRemaining);
	$: holdButtonClass = `hold-delete-btn width-sm-full width-md-auto text-nowrap ${
		isHolding ? 'hold-delete-active' : ''
	}`;
	$: holdProgressStyle = isHolding ? `--hold-progress: ${holdProgress}%` : undefined;

	// Reset whenever the parent closes the modal so a re-open starts clean.
	$: if (!showModal) resetHoldState();

	function getHoldButtonLabel(phase: HoldPhase, secondsRemaining: number): string {
		if (phase === 'complete') {
			return $format('button_release_to_delete_home', { default: 'Release to delete' });
		}
		if (phase === 'holding') {
			return $format('button_keep_holding_delete_home', {
				default: 'Keep holding… {seconds}s',
				values: { seconds: secondsRemaining },
			});
		}
		return $format('button_hold_to_delete_home', { default: 'Hold to delete' });
	}

	function resetHoldState(): void {
		if (activePointerId !== null) {
			tryReleasePointerCapture(activePointerId);
		}
		if (rafId !== undefined) {
			cancelAnimationFrame(rafId);
			rafId = undefined;
		}
		holdPhase = 'idle';
		holdProgress = 0;
		holdSecondsRemaining = HOLD_DURATION_SECONDS;
		holdStartTs = 0;
		activePointerId = null;
	}

	function tickHold(): void {
		const elapsed = performance.now() - holdStartTs;
		holdProgress = Math.min(100, (elapsed / HOLD_DURATION_MS) * 100);
		holdSecondsRemaining = Math.max(1, Math.ceil((HOLD_DURATION_MS - elapsed) / 1000));

		if (elapsed >= HOLD_DURATION_MS) {
			holdPhase = 'complete';
			holdProgress = 100;
			return;
		}

		rafId = requestAnimationFrame(tickHold);
	}

	function trySetPointerCapture(pointerId: number): void {
		if (!holdDeleteHostEl) return;
		try {
			holdDeleteHostEl.setPointerCapture(pointerId);
		} catch {
			// Some environments can throw if capture is not available.
		}
	}

	function tryReleasePointerCapture(pointerId: number): void {
		if (!holdDeleteHostEl) return;
		try {
			if (holdDeleteHostEl.hasPointerCapture(pointerId)) {
				holdDeleteHostEl.releasePointerCapture(pointerId);
			}
		} catch {
			// Ignore release errors from non-captured pointers.
		}
	}

	function isActivePointerEvent(event: PointerEvent): boolean {
		return activePointerId !== null && event.pointerId === activePointerId;
	}

	function onHoldStart(event: PointerEvent): void {
		if (event.button !== 0) return;
		event.preventDefault();
		resetHoldState();
		activePointerId = event.pointerId;
		trySetPointerCapture(event.pointerId);
		holdPhase = 'holding';
		holdStartTs = performance.now();
		rafId = requestAnimationFrame(tickHold);
	}

	function onHoldPointerUp(event: PointerEvent): void {
		if (!isActivePointerEvent(event)) return;
		const shouldDelete = holdPhase === 'complete';
		tryReleasePointerCapture(event.pointerId);
		resetHoldState();
		if (shouldDelete) dispatch('delete');
	}

	function onHoldCancel(event: PointerEvent): void {
		if (!isActivePointerEvent(event)) return;
		tryReleasePointerCapture(event.pointerId);
		resetHoldState();
	}

	function onHoldLeave(event: PointerEvent): void {
		// For mouse, leaving the target should cancel the hold interaction.
		// Touch/pen pointers stay captured and continue until pointerup/cancel.
		if (event.pointerType === 'mouse') {
			onHoldCancel(event);
		}
	}

	function onCancel(): void {
		resetHoldState();
		dispatch('cancel');
	}

	onDestroy(resetHoldState);
</script>

<Modal {showModal} on:close={onCancel} title={modalTitle}>
	<svelte:fragment slot="content">
		<div class="delete-home-modal-content">
			<Stack direction="row" gap="xs" class="mb-m flex-wrap">
				<Text styleType="body-small" class="text-secondary">
					{$format('settings_delete_unit_check_owned_label', {
						default: 'Owned devices: {count}',
						values: { count: ownedCount },
					})}
				</Text>
				<Text styleType="body-small" class="text-secondary">
					{$format('settings_delete_unit_check_shared_label', {
						default: 'Shared to this home: {count}',
						values: { count: sharedCount },
					})}
				</Text>
			</Stack>

			<div class="d-flex mb-m">
				<svelte:component
					this={ButtonComponent}
					variant="primary"
					size="m"
					icon="fas fa-right-left"
					class="width-sm-full width-md-auto text-nowrap"
					aria-label={$format('button_switch_to_home', { default: 'Switch to home' })}
					on:click={() => dispatch('switchToHome')}
				>
					{$format('button_switch_to_home', { default: 'Switch to home' })}
				</svelte:component>
			</div>

			{#if hasOwnedDevices}
				<Text class="mb-s" styleType="body-small">
					{@html $format('settings_delete_unit_check_info_has_devices', {
						default:
							'"<strong><u>{name}</u></strong>" contains {ownedCount} owned device(s). If you delete this home, those devices will be left without an owner and you will need to add them again to a home of your choice. Alternatively, you can switch to this home and transfer the devices to another home you own.',
						values: { name: escapeHtml(unitName), ownedCount },
					})}
				</Text>
				{#if sharedCount > 0}
					<Text styleType="body-small">
						{$format('settings_delete_unit_check_info_has_shared_suffix', {
							default:
								'This home also has {sharedCount} device(s) shared to it; those will lose their access.',
							values: { sharedCount },
						})}
					</Text>
				{/if}
			{:else}
				<Text class="mb-s" styleType="body-small">
					{@html $format('settings_delete_unit_check_info_no_devices', {
						default:
							'"<strong><u>{name}</u></strong>" has no owned devices, but it may still contain rooms, groups, scenes, alarms, or other items you have created. You can switch to this home to review them, or proceed to delete the home directly.',
						values: { name: escapeHtml(unitName) },
					})}
				</Text>
				{#if sharedCount > 0}
					<Text styleType="body-small">
						{$format('settings_delete_unit_check_info_no_owned_shared_line', {
							default:
								'{sharedCount} device(s) are currently shared to this home; access may be affected if you delete it.',
							values: { sharedCount },
						})}
					</Text>
				{/if}
			{/if}

			<Text class="mt-s" styleType="body-small">
				{@html $format('settings_delete_unit_check_confirm_question', {
					default: 'Are you sure you want to delete "<strong><u>{name}</u></strong>"?',
					values: { name: escapeHtml(unitName) },
				})}
			</Text>
		</div>
	</svelte:fragment>

	<div slot="footer" class="footer-actions d-flex gap-s">
		<svelte:component
			this={ButtonComponent}
			variant="secondary"
			size="m"
			class="width-sm-full width-md-auto text-nowrap"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={onCancel}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</svelte:component>

		<!-- Pointer handlers live on the wrapper because design system buttons
		     don't expose pointer events or event modifiers directly. -->
		<div
			bind:this={holdDeleteHostEl}
			class="hold-delete-host"
			role="presentation"
			on:click|preventDefault
			on:contextmenu|preventDefault
			on:pointerdown={onHoldStart}
			on:pointerup={onHoldPointerUp}
			on:pointerleave={onHoldLeave}
			on:pointercancel={onHoldCancel}
		>
			<svelte:component
				this={ButtonComponent}
				variant="danger"
				size="m"
				icon="fas fa-trash"
				class={holdButtonClass}
				style={holdProgressStyle}
				aria-label={holdButtonLabel}
			>
				{#if holdPhase === 'holding'}
					{$format('label_keep_holding_delete_home', { default: 'Keep holding…' })}
					<span class="hold-delete-seconds">{holdSecondsRemaining}</span>s
				{:else}
					{holdButtonLabel}
				{/if}
			</svelte:component>
		</div>
	</div>
</Modal>

<style>
	/* display: contents lets the inner button sit directly in the footer flex
	   row, so it sizes identically to the Cancel button at every breakpoint. */
	.hold-delete-host {
		display: contents;
	}

	.hold-delete-host :global(.hold-delete-btn) {
		position: relative;
		overflow: hidden;
		touch-action: none;
		user-select: none;
		-webkit-user-select: none;
	}

	.hold-delete-host :global(.hold-delete-btn.hold-delete-active::before) {
		content: '';
		position: absolute;
		inset: 0 auto 0 0;
		width: var(--hold-progress, 0%);
		background: rgba(0, 0, 0, 0.2);
		border-radius: inherit;
		pointer-events: none;
		z-index: 0;
	}

	.hold-delete-host :global(.hold-delete-btn .button-text) {
		position: relative;
		z-index: 1;
	}

	.hold-delete-seconds {
		display: inline-block;
		min-width: 1.25em;
		text-align: center;
		font-variant-numeric: tabular-nums;
	}

	@media (min-width: 640px) {
		.footer-actions {
			flex-direction: row;
			justify-content: flex-end;
			align-items: center;
		}
	}

	@media (max-width: 639px) {
		.footer-actions {
			flex-direction: column;
			align-items: stretch;
		}
	}

	/* Prevent long-press text selection in this destructive flow on touch devices. */
	@media (hover: none) and (pointer: coarse) {
		.delete-home-modal-content,
		.footer-actions,
		.footer-actions :global(*) {
			user-select: none;
			-webkit-user-select: none;
			-webkit-touch-callout: none;
		}
	}
</style>
